import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GenericApiService } from '@pm-ui/core-services';
import { Observable } from 'rxjs';
import { map, concatMap } from 'rxjs/operators';
import { TaskAssistantEndpoints } from '../../config/taskAssistant/task-assistant-end-points';

export interface ChatHistoryMessage {
  role: 'user' | 'assistant';
  text: string;
}

export interface TaskQueryRequestDto {
  question: string;
  siteId: number;
  chatHistory?: ChatHistoryMessage[];
  taskId?: number;
  subtaskId?: number;
}

export interface TaskRefineRequestDto {
  siteId: number;
  categoryId?: number;
  buildingId?: number;
  levelId?: number;
  tenancyDetailId?: number;
  taskDescription?: string;
  categoryName?: string;
  buildingName?: string;
  levelName?: string;
  tenancyName?: string;
  taskId?: number;
  /** Plain-text content of the "Add Action / Background" Quill editor */
  background?: string;
  /** Human-readable priority name (e.g. "High", "Medium") */
  priority?: string;
  /** Human-readable name of the selected standard line */
  standardLineName?: string;
  /** Plain-text body of the selected standard line template */
  standardLineTemplate?: string;
  /** Current progress percentage of the task */
  progressPercentage?: number;
  selectedText?: string;
  instruction?: string;
}

export interface TaskRefineResponseDto {
  improvedDescription: string;
  improvedBackground: string;
  refinedText?: string;
  refinedDescription?: string;
  refinedBackground?: string;
  missingInformation: string[];
  guidingQuestions: string[];
  categorySuggestion: string;
  buildingValidation: string;
  scopeImprovements: string[];
  warnings: string[];

  // Phase 2 Fields
  duplicateDetected: boolean;
  duplicateConfidence: number;
  relationshipType: string;
  relatedTasks: Array<{
    taskId: number;
    title: string;
    similarity: number;
    status: string;
    dateRaised?: string;
    reason: string;
  }>;
  recommendations: string[];

  // Mode-specific fields (creation vs closing)
  /** Refinement mode: 'creation' (progress < 100) or 'closing' (progress = 100) */
  mode: string;
  /** Short 5W questions for missing background info — creation mode only (max 3) */
  backgroundQuestions: string[];
  /** Sections with a dash/hyphen placeholder but no content filled in — closing mode */
  unfilledSections: string[];
  suggestedPriority?: string;
  priorityReason?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TaskAssistantService {
  constructor(private genericApiService: GenericApiService, private http: HttpClient) {}

  query(reqModel: TaskQueryRequestDto): Observable<any> {
    return this.genericApiService.post(TaskAssistantEndpoints.Query, reqModel);
  }

  seedSchemas(): Observable<any> {
    return this.genericApiService.post(TaskAssistantEndpoints.SeedMainSchema, {}).pipe(
      concatMap(() => this.genericApiService.post(TaskAssistantEndpoints.SeedSiteSchema, {}))
    );
  }

  refineTask(reqModel: TaskRefineRequestDto): Observable<TaskRefineResponseDto> {
    return this.genericApiService.post<TaskRefineResponseDto>('api/TaskAssistant/Refine', reqModel).pipe(
      map((res: HttpResponse<TaskRefineResponseDto>) => {
        const body = res.body as any;
        if (body) {
          // Map properties for frontend compatibility if needed
          body.improvedDescription = body.improvedDescription || body.improved_description || '';
          body.improvedBackground = body.improvedBackground || body.improved_background || '';
          body.refinedText = body.refinedText || body.refined_text || '';

          // Map new mode-specific fields
          body.mode = body.mode || '';
          body.backgroundQuestions = body.backgroundQuestions || body.background_questions || [];
          body.unfilledSections = body.unfilledSections || body.unfilled_sections || [];
          body.guidingQuestions = body.guidingQuestions || body.guiding_questions || [];
          body.missingInformation = body.missingInformation || body.missing_information || [];
          body.relatedTasks = body.relatedTasks || body.related_tasks || [];
          body.duplicateDetected = body.duplicateDetected ?? body.duplicate_detected ?? false;
          body.scopeImprovements = body.scopeImprovements || body.scope_improvements || [];
          body.categorySuggestion = body.categorySuggestion || body.category_suggestion || 'Correct';
          body.buildingValidation = body.buildingValidation || body.building_validation || 'Correct';
          body.warnings = body.warnings || [];
          body.recommendations = body.recommendations || [];

          // Log token usage from API response (console only — not shown in UI)
          if (body.inputTokens != null || body.outputTokens != null) {
            console.log(
              `[AI Refine] Token usage — Input: ${body.inputTokens ?? 'N/A'}, Output: ${body.outputTokens ?? 'N/A'}, Total: ${body.totalTokens ?? 'N/A'}`
            );
          }
        }
        return body;
      })
    );
  }

  checkDuplicates(reqModel: TaskRefineRequestDto): Observable<any[]> {
    return this.genericApiService.post<any[]>('api/TaskAssistant/CheckDuplicates', reqModel).pipe(
      map((res: HttpResponse<any[]>) => {
        return res.body || [];
      })
    );
  }
}
